// use-subscription — lädt die verfügbaren Credit-Abo-Pläne + das aktive Abo.
// Quelle: GET /subscriptions/me → { plans, active } (Bearer-Auth).
// Refetch: on-mount + bei `window`-Event 'eve:credits-changed' (analog use-credits).

import { useCallback, useEffect, useState } from 'react';
import { ApiError, apiFetch } from '@/lib/api';
import { selectAccessToken, useAuthStore } from '@/stores/auth-store';
import { CREDITS_CHANGED_EVENT } from './use-credits';
import type {
  ActiveSubscription,
  SubscriptionMeResponse,
  SubscriptionPlan,
} from '@/lib/subscriptions-api';

export interface UseSubscriptionReturn {
  plans: ReadonlyArray<SubscriptionPlan>;
  active: ActiveSubscription | null;
  loading: boolean;
  refresh: () => void;
}

export function useSubscription(): UseSubscriptionReturn {
  const accessToken = useAuthStore(selectAccessToken);
  const [plans, setPlans] = useState<ReadonlyArray<SubscriptionPlan>>([]);
  const [active, setActive] = useState<ActiveSubscription | null>(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback((): void => {
    if (!accessToken) {
      setPlans([]);
      setActive(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    void (async () => {
      try {
        const data = await apiFetch<SubscriptionMeResponse>('/subscriptions/me', { accessToken });
        setPlans(Array.isArray(data.plans) ? data.plans : []);
        setActive(data.active ?? null);
      } catch (e) {
        const msg = e instanceof ApiError ? e.message : e instanceof Error ? e.message : 'subscription load failed';
        console.warn('[subscription] fetch failed:', msg);
      } finally {
        setLoading(false);
      }
    })();
  }, [accessToken]);

  // Mount + token-change: initial laden.
  useEffect(() => {
    refresh();
  }, [refresh]);

  // Refetch nach Credit-/Abo-Änderung (gleicher Event wie use-credits).
  useEffect(() => {
    const onChanged = (): void => refresh();
    window.addEventListener(CREDITS_CHANGED_EVENT, onChanged);
    return () => window.removeEventListener(CREDITS_CHANGED_EVENT, onChanged);
  }, [refresh]);

  return { plans, active, loading, refresh };
}
